import React, { useState, useRef, useEffect } from 'react';

interface InputAreaProps {
  onSendMessage: (message: string) => void;
  isSearching: boolean;
  searchStatus: string;
}

const InputArea: React.FC<InputAreaProps> = ({ 
  onSendMessage, 
  isSearching,
  searchStatus
}) => {
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 120; // 6 lines approximately

      if (scrollHeight <= maxHeight) {
        textarea.style.height = Math.max(24, scrollHeight) + 'px';
      } else {
        textarea.style.height = maxHeight + 'px';
      }
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  const sendMessage = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage) {
      onSendMessage(trimmedMessage);
      setMessage('');
    }
  };

  const canSend = message.trim().length > 0;

  return (
    <div className="input-container">
      <div className="input-field">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          placeholder="What questions do you have about your dog today?"
          rows={1}
          maxLength={2000}
        />

        <button
          className="send-button"
          onClick={sendMessage}
          disabled={!canSend}
        >
          <i className="fas fa-paper-plane"></i>
        </button>
      </div>
      <div className="input-footer">
        <div className="input-hint">
          <span className="keyboard-hint">Press Enter to send • Shift+Enter for new line</span>
          <span className="ai-disclaimer">
            <i className="fas fa-info-circle"></i>
            AI can make mistakes - consult a veterinarian for health decisions
          </span>
        </div>
      </div>
    </div>
  );
};

export default InputArea;
